import styled, { css } from "styled-components";

interface IQuestionsPillProps {
  /** Toggles the chat widget open and closed. */
  onClick: () => void;
  /** Accessible label — the chat's title, e.g. "Clear Health AI". */
  ariaLabel?: string;
  /** True while the panel this pill toggles is open. */
  isOpen?: boolean;
  /** Renders in the document flow instead of pinned to the bottom-right. */
  inline?: boolean;
  /** Visible copy on the pill. */
  label?: string;
  className?: string;
}

const Pill = styled.button<{ $inline: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  /* Same box as the "Powered by" pill it replaces, so the chat panel and the
     page's bottom padding — both keyed off these vars — stay aligned. */
  height: var(--powered-by-badge-height);
  padding: 0 24px;
  border: none;
  border-radius: 999px;

  background: var(--brand-purple-700);
  color: var(--brand-white);
  box-shadow: 0 8px 24px rgba(79, 70, 229, 0.32);

  cursor: pointer;
  user-select: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  ${({ $inline }) =>
    !$inline &&
    css`
      position: fixed;
      right: var(--powered-by-badge-offset);
      bottom: var(--powered-by-badge-offset);
      z-index: 900;
    `}

  svg {
    width: 20px;
    height: 20px;
  }

  :hover {
    background: var(--brand-purple-600);
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(79, 70, 229, 0.4);
  }

  @media (max-width: 720px) {
    padding: 0 16px;
    gap: 8px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Label = styled.span`
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 720px) {
    font-size: 0.875rem;
  }
`;

function ChatBubbleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

/**
 * The "Questions?" pill — the chat widget's collapsed button. The fixed staff
 * widget renders it inline in its corner row (ChatWidget).
 */
export function QuestionsPill({
  onClick,
  ariaLabel,
  isOpen = false,
  inline = false,
  label = "Questions?",
  className,
}: IQuestionsPillProps): JSX.Element {
  return (
    <Pill
      type="button"
      className={className}
      $inline={inline}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={isOpen}
    >
      <ChatBubbleIcon />
      <Label>{label}</Label>
    </Pill>
  );
}
